import resolve from '../object/resolve.js';
import merge from '../object/merge.js';

/**
 * mergeArray(array, newArray, valueMap)
 *
 * Returns a new array where, for each element in `array`, matching elements from
 * `newArray` (matched by id) are used to overwrite fields according to `valueMap`.
 *
 * Matching:
 * - newArray elements are indexed by `id`
 * - base array elements are matched by:
 *    - item.id if present
 *    - else item.itemId if present
 *    - else item._id if present
 *
 * Overwrite rules:
 * - valueMap is an object mapping { targetPathInArrayItem: sourcePathInNewArrayItem }
 * - For each match, resolve(sourcePath, matchedNewItem) is computed and merged into
 *   the array item at targetPath using merge() (supports nested paths).
 * - If the resolved value is undefined or null, it will overwrite with that value
 *   as well (explicitly allowing nulls). If you want to skip null/undefined, add
 *   a guard before calling merge().
 *
 * Non-mutating: Original `array` and `newArray` are not modified. The function returns
 * a new array with updated items (shallow-copied and merged per item).
 *
 * @param {Array} array - Base array to merge into (e.g., existing items).
 * @param {Array} newArray - Array providing new values (must contain `id`).
 * @param {Object} valueMap - Mapping of targetPath -> sourcePath to copy values.
 * @returns {Array} New merged array.
 */
function mergeArray(array, newArray, valueMap) {
  const base = Array.isArray(array) ? array : [];
  const incoming = Array.isArray(newArray) ? newArray : [];
  const map = isPlainObject(valueMap) ? valueMap : {};

  // Index newArray by id
  const byId = new Map();
  for (const it of incoming) {
    const id = safeId(it, true); // for newArray prefer strict 'id'
    if (id !== null) {
      byId.set(id, it);
    }
  }

  // Merge each base item if matching id found
  return base.map((item) => {
    const id = safeId(item, false); // for base try id, itemId, _id
    if (id === null) {
      // No match key available; return a shallow copy
      return { ...item };
    }
    const match = byId.get(id);
    if (!match) {
      // No incoming match; return a shallow copy
      return { ...item };
    }

    // Start with a shallow copy to keep function non-mutating
    let out = { ...item };

    for (const [targetPath, sourcePath] of Object.entries(map)) {
      // Resolve from matched new item
      const val = resolve(sourcePath, match);
      // Merge into the output at the target path (creates containers as needed)
      out = merge(out, val, targetPath);
    }
    return out;
  });
}

function isPlainObject(val) {
  return typeof val === 'object' && val !== null && !Array.isArray(val);
}

/**
 * Obtain a stable string id from an object. For newArray items, only 'id' is considered.
 * For base items (array), allow id, itemId, or _id to support common shapes.
 */
function safeId(obj, strictIdOnly = false) {
  if (obj == null || typeof obj !== 'object') return null;

  if (obj.id !== undefined && obj.id !== null) {
    return String(obj.id);
  }
  if (!strictIdOnly) {
    if (obj.itemId !== undefined && obj.itemId !== null) {
      return String(obj.itemId);
    }
    if (obj._id !== undefined && obj._id !== null) {
      return String(obj._id);
    }
  }
  return null;
}

export default mergeArray;
